import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { FormattedMessage } from 'react-intl';
import { type ConnectedProps, connect } from 'react-redux';

import { optionsActions, type VehicleAttributes } from '../../../application/redux/options/optionsReducer';
import {
    getHasCustomAttributes,
    getSelectedProfile,
    getVehicleAttributes,
} from '../../../application/redux/options/optionsSelectors';
import { routesActions } from '../../../application/redux/routes/routesReducer';
import { gaPush, TRACKING_CATEGORIES } from '../../../configuration/setup/googleTagManager';
import type { AppDispatch, RootState } from '../../../configuration/setup/store';
import { AxleInputs } from './AxleInputs';
import { EmissionAndHazardSelects } from './EmissionAndHazardSelects';
import type { Profile } from './VehicleAttributePresets';
import { VehicleDimensionsInputs } from './VehicleDimensionInputs';
import { VehicleTypeSelect } from './VehicleTypeSelect';
import { WeightInputs } from './WeightInputs';

export const VehicleOptions = (props: VehicleOptionsProps) => {
    const { selectedProfile, hasCustomAttributes, vehicleAttributes, onProfileChange, onVehicleAttributesUpdate } =
        props;

    const {
        length,
        height,
        width,
        limitedWeight,
        weightPerAxle,
        vehicleNumberAxles,
        trailerNumberAxles,
        emissionType,
        shippedHazardousGoods,
    } = vehicleAttributes;

    const handleExpanderPanelToggle = (value: boolean) => {
        gaPush({
            category: TRACKING_CATEGORIES.TOUR_OPTIONS_EDITOR,
            action: 'Vehicle Options Expander Toggled',
            label: `Toggled Vehicle Options Expander::${value ? 'Open' : 'Closed'}`,
        });
    };

    return (
        <ExpanderPanel
            className={'VehicleOptions margin-bottom-0'}
            title={<FormattedMessage id={'intl-msg:routeplanning.label.vehicleOptions'} />}
            iconLeft
            bsStyle={'separator'}
            onToggle={handleExpanderPanelToggle}
        >
            <VehicleTypeSelect
                profile={selectedProfile}
                onProfileChange={onProfileChange}
                hasCustomAttributes={hasCustomAttributes}
            />
            <VehicleDimensionsInputs
                totalLength={length}
                height={height}
                width={width}
                onVehicleAttributesUpdate={onVehicleAttributesUpdate}
            />
            <WeightInputs
                maxWeight={limitedWeight}
                maxAxleLoad={weightPerAxle}
                onVehicleAttributesUpdate={onVehicleAttributesUpdate}
            />
            <AxleInputs
                vehicleAxles={vehicleNumberAxles}
                trailerAxles={trailerNumberAxles}
                onVehicleAttributesUpdate={onVehicleAttributesUpdate}
            />
            <EmissionAndHazardSelects
                emissionClass={emissionType}
                hazardClass={shippedHazardousGoods}
                onVehicleAttributesUpdate={onVehicleAttributesUpdate}
            />
        </ExpanderPanel>
    );
};

type VehicleOptionsProps = ConnectedProps<typeof connector>;

export const mapStateToProps = (state: RootState) => ({
    selectedProfile: getSelectedProfile(state),
    hasCustomAttributes: getHasCustomAttributes(state),
    vehicleAttributes: getVehicleAttributes(state),
});

export const mapDispatchToProps = (dispatch: AppDispatch) => ({
    onProfileChange: (profile: Profile) => {
        dispatch(optionsActions.selectedProfileChanged(profile));
        dispatch(routesActions.suggestedRoutesRemoved());
    },
    onVehicleAttributesUpdate: (attributes: Partial<VehicleAttributes>) => {
        dispatch(optionsActions.vehicleAttributesUpdated(attributes));
        dispatch(routesActions.suggestedRoutesRemoved());
    },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export const VehicleOptionsContainer = connector(VehicleOptions);
